import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { api, money } from "@/lib/api";
import { Eyebrow } from "@/components/Motion";
import PageHero from "@/components/PageHero";

const field = "w-full bg-transparent border-b border-matte/20 py-3 focus:outline-none focus:border-matte transition-colors";

export default function Enroll() {
  const [params] = useSearchParams();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    parent_name: "", child_name: "", child_dob: "", phone: "", email: "",
    address: "", emergency_contact: "", medical_notes: "",
    program_id: params.get("program") || "", agree_policies: false,
  });

  useEffect(() => {
    api.get("/programs").then((r) => {
      setPrograms(r.data);
      if (!form.program_id && r.data[0]) setForm((f) => ({ ...f, program_id: r.data[0].id }));
    });
    // eslint-disable-next-line
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const selected = programs.find((p) => p.id === form.program_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agree_policies) {
      toast.error("Please agree to the policies to continue");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/checkout/enrollment", { ...form, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Enrollment failed");
      setLoading(false);
    }
  };

  return (
    <div data-testid="enroll-page">
      <PageHero
        eyebrow="Enrollment"
        title="Reserve your child's place at Brainwaves."
        image=""
        height="55vh"
      />
      <div className="max-w-[1000px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <form onSubmit={submit} className="space-y-12" data-testid="enroll-form">
          <div>
            <p className="text-[11px] uppercase tracking-editorial text-plum mb-6">Parent / Guardian</p>
            <div className="grid md:grid-cols-2 gap-6">
              <input required placeholder="Parent name" value={form.parent_name} onChange={set("parent_name")} className={field} data-testid="enroll-parent-name" />
              <input required placeholder="Phone" value={form.phone} onChange={set("phone")} className={field} data-testid="enroll-phone" />
              <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={field} data-testid="enroll-email" />
              <input required placeholder="Emergency contact" value={form.emergency_contact} onChange={set("emergency_contact")} className={field} data-testid="enroll-emergency" />
              <input required placeholder="Home address" value={form.address} onChange={set("address")} className={`${field} md:col-span-2`} data-testid="enroll-address" />
            </div>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-editorial text-plum mb-6">Child</p>
            <div className="grid md:grid-cols-2 gap-6">
              <input required placeholder="Child name" value={form.child_name} onChange={set("child_name")} className={field} data-testid="enroll-child-name" />
              <div>
                <label className="text-[10px] uppercase tracking-wide-sm text-matte/40">Date of birth</label>
                <input required type="date" value={form.child_dob} onChange={set("child_dob")} className={field} data-testid="enroll-child-dob" />
              </div>
              <textarea rows={3} placeholder="Medical notes / allergies (optional)" value={form.medical_notes} onChange={set("medical_notes")} className={`${field} md:col-span-2 resize-none`} data-testid="enroll-medical" />
            </div>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-editorial text-plum mb-6">Preferred Program</p>
            <div className="grid gap-3">
              {programs.map((p) => (
                <label
                  key={p.id}
                  className={`flex items-center justify-between gap-4 border p-5 cursor-pointer transition-colors ${
                    form.program_id === p.id ? "border-plum bg-cream" : "border-matte/15 hover:border-matte/40"
                  }`}
                  data-testid={`enroll-program-${p.id}`}
                >
                  <div className="flex items-center gap-4">
                    <input type="radio" name="program" checked={form.program_id === p.id} onChange={() => setForm({ ...form, program_id: p.id })} className="accent-plum" />
                    <div>
                      <p className="font-serif text-xl">{p.title}</p>
                      <p className="text-[11px] uppercase tracking-wide-sm text-matte/50">{p.schedule}</p>
                    </div>
                  </div>
                  <span className="font-serif text-xl text-plum">{money(p.price)}</span>
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={form.agree_policies} onCheckedChange={(v) => setForm({ ...form, agree_policies: !!v })} data-testid="enroll-agree" className="mt-1" />
            <span className="text-sm text-matte/60 leading-relaxed">
              I agree to the Brainwaves enrollment policies, including scheduling, cancellation, and safety guidelines.
            </span>
          </label>

          <button
            type="submit" disabled={loading}
            data-testid="enroll-submit-btn"
            className="bg-matte text-ivory px-12 py-4 text-[12px] uppercase tracking-editorial hover:bg-plum transition-colors duration-300 disabled:opacity-50"
          >
            {loading ? "Redirecting…" : selected ? `Enroll & Pay · ${money(selected.price)}` : "Enroll & Pay"}
          </button>
        </form>
      </div>
    </div>
  );
}
